import React from 'react'
import { GripVertical, Trash2 } from 'lucide-react'
import { cn } from '../lib/utils'

const QueueItemCard = React.forwardRef(
  (
    {
      item,
      handleRemoveFromQueue,
      onDragHandlePointerDown,
      isDragging = false,
      style = {},
      disableRemove = false,
      showDragHandle = true,
      timeLabel = null
    },
    ref
  ) => {
    const youtubeId = item.song?.youtubeId ?? null
    const thumbnailUrl = youtubeId ? `/uploads/songs/${youtubeId}/thumbnail.webp` : null
    const [thumbnailLoaded, setThumbnailLoaded] = React.useState(false)
    const shouldShowStatusBadge = item.status === 'playing' || item.status === 'failed' || item.status === 'processing'
    const statusLabel = item.status === 'playing'
      ? 'Now Playing'
      : (item.status === 'failed' ? 'Failed' : 'Processing')

    React.useEffect(() => {
      if (!thumbnailUrl) {
        setThumbnailLoaded(false)
        return
      }

      const image = new Image()
      image.onload = () => setThumbnailLoaded(true)
      image.onerror = () => setThumbnailLoaded(false)
      image.src = thumbnailUrl
    }, [thumbnailUrl])

    const removable = !disableRemove && item.status !== 'playing'

    return (
      <article
        ref={ref}
        style={style}
        className={cn(
          'relative overflow-hidden rounded-2xl border border-white/15 bg-[color:rgba(16,27,45,0.9)]',
          'transition-[box-shadow,border-color,background-color] duration-200 ease-out',
          isDragging ? 'z-50 shadow-[0_18px_38px_rgba(2,8,18,0.55)]' : 'shadow-[0_10px_24px_rgba(2,8,18,0.35)]',
          isDragging ? 'select-none' : ''
        )}
      >
        {thumbnailLoaded ? (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `linear-gradient(104deg, rgba(3,9,18,0.9), rgba(3,9,18,0.36)), url(${thumbnailUrl})`
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-[linear-gradient(145deg,#22d3ee_0%,#2563eb_40%,#1e3a8a_100%)] opacity-35" />
        )}

        <div className="relative z-10 flex min-h-[92px] items-center gap-2 px-3 py-3">
          {showDragHandle ? (
            <button
              type="button"
              className={cn(
                'room-focus-ring flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border',
                item.status !== 'playing'
                  ? 'cursor-grab border-white/20 bg-white/10 text-[var(--room-muted)] hover:bg-white/15 hover:text-[var(--room-text)]'
                  : 'cursor-not-allowed border-white/10 bg-black/20 text-white/35'
              )}
              style={{
                touchAction: item.status !== 'playing' ? 'none' : 'auto'
              }}
              onPointerDown={(event) => {
                if (item.status === 'playing' || typeof onDragHandlePointerDown !== 'function') {
                  return
                }

                onDragHandlePointerDown(event)
              }}
              aria-label={item.status === 'playing' ? 'Cannot reorder currently playing song' : 'Drag to reorder'}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          ) : null}

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <h3 className="truncate text-sm font-semibold text-[var(--room-text)]">{item.song?.title}</h3>
              {shouldShowStatusBadge ? (
                <span
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.05em]',
                    item.status === 'playing'
                      ? 'border-[color:rgba(23,214,163,0.45)] bg-[color:rgba(23,214,163,0.2)] text-[var(--room-success)]'
                      : 'border-white/20 bg-white/10 text-[var(--room-muted)]'
                  )}
                >
                  {statusLabel}
                </span>
              ) : null}
            </div>
            <p className="truncate text-xs text-[var(--room-muted)]">{item.song?.artist}</p>
            {timeLabel ? (
              <p className="mt-1 text-[11px] font-medium text-[var(--room-primary)]">{timeLabel}</p>
            ) : null}
          </div>

          {removable ? (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                handleRemoveFromQueue?.(item.id)
              }}
              onPointerDown={(event) => event.stopPropagation()}
              className="room-focus-ring flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[color:rgba(255,102,122,0.35)] bg-[color:rgba(255,102,122,0.14)] text-[var(--room-danger)] hover:bg-[color:rgba(255,102,122,0.24)]"
              aria-label={`Remove ${item.song?.title ?? 'song'} from queue`}
              title="Remove from queue"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </article>
    )
  }
)

QueueItemCard.displayName = 'QueueItemCard'

export default QueueItemCard
